import React from 'react'

const Recur = () => {
    return (
        <div className='container'>
            <h1>Recursion in C/C++</h1>
            <p>Recursion in C/C++ is nothing but a repetitive process to accomplish a particular task. Iteration and recursion somewhat serve the same purpose. After completing this tutorial, you will understand why we use recursion instead of iteration in some cases.</p>
            <h2 >1. What is Recursion?</h2>
            <p>Recursion refers to<em> the processes of repetition of a particular task in order to achieve a specific purpose</em>. Therefore, it is safe to say that a recursive function in C/C++ allows the programmer to call the same function within itself.&nbsp;In other words, <em>a recursive function is simply a function that calls itself. It is terminated when the main condition no longer continues to be satisfied</em>.</p>
            <p>Employing if-else conditions to control recursion is a good programming practice. The basic idea behind recursion in C/C++ is to break the main problem at hand into smaller fragments that follow a logical sequence.</p>
            <p>It is important to note that<strong><em> Iteration (Looping) and Recursion are totally two different concepts</em></strong> which cannot be confused at any cost.</p>
            <h2>2. Significance of Recursion Function in C/C++</h2>
            <p>One of the striking features of recursion is its ability to enhance code readability by keeping it short and sweet. However, it is a method you would not prefer to use if you want to reduce the run-time of your code. Iteration is generally a much faster process than compared to recursion.</p>
            <p>There are a wide variety of problems that we can solve using recursion such as to compute the factorial of a number, to find the Fibonacci series in a given range, the various kinds of tree traversals and the famous Tower of Hanoi problem.</p>
            <h2 >3. How Does Recursion in C/C++ Work?</h2>
            <p>The working of C/C++ recursive function is pretty simple. This is how it works:</p>
            <div class="containers" >
        <div class="">
                <div><span class="plain">void </span><span class="keywords">recursive_function</span><span
                                class="plain">()</span></div>
        </div>
        <div class="">
                <div><span class="plain">&#123;</span></div>
        </div>
        <div class="">
                <div><span class="keywords">recursive_function</span><span
                                class="plain">()</span><span class="plain">;</span><span
                                class="comment"> // Here the function calls itself</span></div>
        </div>
        <div class="">
                <div><span class="plain">&#125;</span></div>
        </div>
        <div class="">
                <div></div>
        </div>
        <div class="">
                <div><span class="plain">int </span><span class="keywords">main</span><span
                                class="plain">()</span></div>
        </div>
        <div class="">
                <div><span class="plain">&#123;</span></div>
        </div>
        <div class="">
                <div><span class="keywords">recursive_function</span><span
                                class="plain">()</span><span class="plain">;</span></div>
        </div>
        <div class="">
                <div><span class="enlighter-k1">return</span><span
                                class="plain"> </span><span class="enlighter-n1">0</span><span
                                class="plain">;</span></div>
        </div>
        <div class="">
                <div><span class="plain">&#125;</span></div>
        </div>
</div>
<p>In order to understand how a recursive function really works, let us consider a standard problem where we can apply the concept of recursion.</p>
<p>Let us create a recursive function to compute the factorial of a number.</p>
<p>We know that factorial of a number n is given by 1 x 2 x 3 x …… x n</p>
<h4>Example of Recursion in C++</h4>
<p>Here is a program in C++ that computes the factorial of a number using recursion:</p>
<div class="containers">
        <div class="">
                <div><span class="library">#include&lt;iostream&gt;</span><span class="keywords"></span>
                </div>
        </div>
        <div class="">
                <div><span class="keywords">using namespace std;</span></div>
        </div>
        <div class="">
                <div><span class="keywords">int </span><span class="plain">factorial</span><span
                                class="plain">(</span><span class="keywords">int number</span><span
                                class="plain">)</span><span class="keywords"></span></div>
        </div>
        <div class="">
                <div><span class="keywords"></span><span class="plain">&#123;</span><span
                                class="keywords"></span></div>
        </div>
        <div class="">
                <div><span class="keywords"></span><span class="k1">if</span><span
                                class="keywords"> </span><span class="plain">(</span><span
                                class="keywords">number </span><span class="plain">&gt;</span><span
                                class="keywords"> </span><span class="n1">0</span><span
                                class="plain">)</span><span class="keywords"></span></div>
        </div>
        <div class="">
                <div><span class="keywords"></span><span class="plain">&#123;</span><span
                                class="keywords"></span></div>
        </div>
        <div class="">
                <div><span class="keywords"></span><span class="k1">return</span><span
                                class="keywords"> number*</span><span class="plain">factorial</span><span
                                class="plain">(</span><span class="keywords">number-</span><span
                                class="n1">1</span><span class="plain">)</span><span
                                class="keywords">;</span></div>
        </div>
        <div class="">
                <div><span class="keywords"></span><span class="plain">&#125;</span><span
                                class="keywords"></span></div>
        </div>
        <div class="">
                <div><span class="keywords"></span><span class="k1">else</span><span
                                class="keywords"></span></div>
        </div>
        <div class="">
                <div><span class="keywords"></span><span class="plain">&#123;</span><span
                                class="keywords"></span></div>
        </div>
        <div class="">
                <div><span class="keywords"></span><span class="k1">return</span><span
                                class="keywords"> </span><span class="n1">1</span><span
                                class="keywords">;</span></div>
        </div>
        <div class="">
                <div><span class="keywords"></span><span class="plain">&#125;</span><span
                                class="keywords"></span></div>
        </div>
        <div class="">
                <div><span class="keywords"></span><span class="plain">&#125;</span><span
                                class="keywords"></span></div>
        </div>
        <div class="">
                <div><span class="keywords">int </span><span class="plain">main</span><span
                                class="plain">()</span><span class="keywords"></span></div>
        </div>
        <div class="">
                <div><span class="keywords"></span><span class="plain">&#123;</span><span
                                class="keywords"></span></div>
        </div>
        <div class="">
                <div><span class="keywords"></span></div>
        </div>
        <div class="">
                <div><span class="keywords">cout</span><span class="plain">&lt;&lt;</span><span
                                class="s0">"Welcome to DataFlair tutorials!"</span><span
                                class="plain">&lt;&lt;</span><span class="keywords">endl</span><span
                                class="plain">&lt;&lt;</span><span class="keywords">endl;</span></div>
        </div>
        <div class="">
                <div><span class="keywords"></span></div>
        </div>
        <div class="">
                <div><span class="keywords">int number;</span></div>
        </div>
        <div class="">
                <div><span class="keywords">cout</span><span class="plain">&lt;&lt;</span><span
                                class="s0">"Enter a positive integer: "</span><span
                                class="keywords">;</span></div>
        </div>
        <div class="">
                <div><span class="keywords">cin</span><span class="plain">&gt;&gt;</span><span
                                class="keywords">number;</span></div>
        </div>
        <div class="">
                <div><span class="keywords">cout</span><span class="plain">&lt;&lt;</span><span
                                class="s0">"The factorial of the number: "</span><span
                                class="plain">&lt;&lt;</span><span class="keywords"> number </span><span
                                class="plain">&lt;&lt;</span><span class="s0">" is: "</span><span
                                class="plain">&lt;&lt;</span><span class="keywords"> </span><span
                                class="plain">factorial</span><span class="plain">(</span><span
                                class="keywords">number</span><span class="plain">)&lt;&lt;</span><span
                                class="keywords"> endl;</span></div>
        </div>
        <div class="">
                <div><span class="keywords"></span><span class="k1">return</span><span
                                class="keywords"> </span><span class="n1">0</span><span
                                class="keywords">;</span></div>
        </div>
        <div class="">
                <div><span class="keywords"></span><span class="plain">&#125;</span></div>
        </div>
</div>
<p><strong>Elucidation:</strong></p>
<ul><li>We create a function named factorial to compute the factorial of a positive integer using recursion with 1 argument with the return type integer.</li>
<li>The number whose factorial is to be computed is given as a parameter to the function.</li>
<li>If the number is equal to 0, the return value of the result would be 1. The only case would be number = 0 and we know that the factorial of 0 is 1.</li>
<li>If the number is greater than 0, that is, the general case, we are supposed to multiply the number entered by the user with its preceding number, that is, number-1 till the number comes 1.</li>
</ul>
<p>Suppose the user entered number = 5</p>
<p>Therefore, after each recursion, the value returned would be:</p>
<p>5 * factorial(4)<br/>
 5 * 4 * factorial(3)<br/>
 5 * 4 * 3 * factorial(2)<br/>
 5 * 4 * 3 * 2 * factorial(1)<br/>
 5 * 4 * 3 * 2 * 1 *factorial(0)</p>
 <p>Here, when the number becomes 0, the condition terminates and we get the output as 120.</p>
 <h4>&nbsp;Advantages</h4>
 <ul>
     <li>Recursive functions in C enhance the readability of the program.</li>
     <li>Used to solve problems that are inherently recursive in nature such as tree traversal problems and the famous Tower of Hanoi problem.</li>
</ul>
<h4>Disadvantages</h4>
<ul>
    <li>It requires a lot of storage space hence the program is comparatively slower than iterative functions.</li>
    <li>The time complexity of recursion is greater than iteration.</li>
</ul>

        </div>
    )
}

export default Recur
